import type { ErrorLike } from "@apollo/client";
import { CombinedGraphQLErrors } from "@apollo/client/errors";

type GraphQLErrorEntry = CombinedGraphQLErrors["errors"][number];

/**
 * Filters GraphQL errors out of an Apollo error, rewriting nothing it need not.
 * `isFilteredOut` judges each entry. Returns:
 *
 * - `undefined` when there is no error or when nothing survives — an envelope
 *   with an empty `errors` array (which Apollo never constructs) counts as
 *   nothing surviving;
 * - the ORIGINAL error object when it is not a GraphQL error, or when no entry
 *   was filtered out — identity, stack, and the response `data`/`extensions`
 *   it carries stay intact;
 * - a new CombinedGraphQLErrors over the survivors, carrying the original
 *   response `data` and `extensions`, when only some entries were filtered out
 *   (its message is re-formatted over the survivors and its stack is fresh).
 *
 * The package's filters are predicates over this one contract.
 */
export const filterOutGraphQLErrors = (
  error: ErrorLike | undefined,
  isFilteredOut: (entry: GraphQLErrorEntry) => boolean,
): ErrorLike | undefined => {
  if (!error) {
    return undefined;
  }

  if (!CombinedGraphQLErrors.is(error)) {
    return error;
  }

  const surviving = error.errors.filter((entry) => !isFilteredOut(entry));

  if (!surviving.length) {
    return undefined;
  }

  if (surviving.length === error.errors.length) {
    return error;
  }

  return new CombinedGraphQLErrors({
    data: error.data,
    errors: surviving,
    extensions: error.extensions,
  });
};
